import * as React from "react";
import { Link } from "react-router-dom";
import {
  User, Mail, Calendar, Trophy, Flame, ShieldCheck, LogOut,
  KeyRound, Link2, BellRing, Trash2, ChevronRight, Save, Loader2, AlertCircle, CheckCircle2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { BlurReveal } from "@/components/effects/blur-reveal";
import { AmbientCanvas } from "@/components/effects/ambient-canvas";
import { PageFrame } from "@/components/page-frame";
import { AppTopBar } from "@/components/app-topbar";
import { useAuth } from "@/lib/auth-context";
import { fetchLeaderboard } from "@/lib/queries";
import { updateProfile } from "@/lib/queries";
import { supabase } from "@/lib/supabase";

export default function Profile() {
  const { user, access, refreshAccess, signOut } = useAuth();
  const profile = access?.profile;

  const [name, setName]   = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [msg, setMsg]     = React.useState<{ kind: "ok" | "err"; text: string } | null>(null);
  const [rank, setRank]   = React.useState<number | null>(null);
  const [streak, setStreak] = React.useState(0);

  React.useEffect(() => { setName(profile?.full_name || ""); }, [profile?.full_name]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const board = await fetchLeaderboard(200);
      if (cancelled) return;
      const sorted = [...board].sort((a, b) => b.total_points - a.total_points);
      const me = sorted.findIndex(r => r.id === user?.id);
      if (me >= 0) setRank(me + 1);
      const mineSt = board.find(r => r.id === user?.id);
      if (mineSt) setStreak(mineSt.streak);
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    setMsg(null);
    setSaving(true);
    try {
      await updateProfile({ full_name: name.trim() });
      await refreshAccess();
      setMsg({ kind: "ok", text: "Saved." });
    } catch (e: any) {
      setMsg({ kind: "err", text: e?.message || "Couldn't save right now." });
    } finally {
      setSaving(false);
    }
  }

  async function onDelete() {
    if (!confirm("Delete account? You have 30 days to recover before it's wiped.")) return;
    try {
      const { error } = await supabase.rpc("request_account_deletion");
      if (error) throw error;
      await signOut("/signed-out");
    } catch (e: any) {
      alert(e?.message || "Couldn't request deletion. Email support@arabise.co.uk.");
    }
  }

  const joined = user?.created_at
    ? new Date(user.created_at).toLocaleDateString("en-GB", { month: "short", year: "numeric" })
    : "—";
  const initial = ((name || user?.email || "?").trim()[0] || "?").toUpperCase();

  return (
    <PageFrame surface="app">
      <AppTopBar />
      <div className="relative">
        <AmbientCanvas className="absolute inset-0 -z-10 w-full h-[800px] pointer-events-none" />
        <div className="relative mx-auto max-w-5xl px-4 md:px-6 pt-10 pb-32">
        <BlurReveal>
          <header className="rounded-3xl border border-line bg-bg-2 p-6 md:p-8 relative overflow-hidden">
            <div aria-hidden className="absolute -right-8 -top-12 w-48 h-48 rounded-full bg-accent/10 blur-3xl" />
            <div className="relative flex flex-col md:flex-row items-start md:items-center gap-6">
              {profile?.avatar_url ? (
                <img src={profile.avatar_url} alt="" className="w-24 h-24 rounded-3xl object-cover border border-line" />
              ) : (
                <div className="inline-flex items-center justify-center w-24 h-24 rounded-3xl bg-accent/10 border border-accent-lo text-accent text-3xl font-bold">
                  {initial}
                </div>
              )}
              <div className="flex-1">
                <div className="text-xs uppercase tracking-widest text-fg-3">Profile</div>
                <h1 className="text-2xl md:text-3xl font-bold tracking-tight mt-1">
                  {name || user?.email?.split("@")[0] || "Your profile"}
                </h1>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-fg-2">
                  <Mail className="w-4 h-4" /> {user?.email}
                  <span className="text-fg-3">·</span>
                  <Calendar className="w-4 h-4" /> Joined {joined}
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {streak > 0 && <Badge variant="accent"><Flame className="w-3 h-3" /> {streak}-day streak</Badge>}
                  {rank && <Badge variant="default"><Trophy className="w-3 h-3" /> Rank #{rank}</Badge>}
                  <Badge variant="success"><ShieldCheck className="w-3 h-3" /> {profile?.license_status?.replace("license_", "") || "no plan"}</Badge>
                </div>
              </div>
              <Button variant="outline" size="sm" className="md:self-start" onClick={() => signOut()}>
                <LogOut className="w-4 h-4" /> Sign out
              </Button>
            </div>
          </header>
        </BlurReveal>

        <div className="mt-8 grid md:grid-cols-3 gap-3">
          <BlurReveal>
            <Card>
              <CardContent>
                <div className="text-xs uppercase tracking-widest text-fg-3">Total points</div>
                <div className="text-3xl font-bold tracking-tight mt-1">{(profile?.total_points ?? 0).toLocaleString()}</div>
                <div className="text-xs text-fg-2 mt-1">Across every mode</div>
              </CardContent>
            </Card>
          </BlurReveal>
          <BlurReveal>
            <Card>
              <CardContent>
                <div className="text-xs uppercase tracking-widest text-fg-3">Current streak</div>
                <div className="text-3xl font-bold tracking-tight mt-1">{streak} {streak === 1 ? "day" : "days"}</div>
                <div className="text-xs text-fg-2 mt-1">{streak === 0 ? "Light it today" : "Keep it alive"}</div>
              </CardContent>
            </Card>
          </BlurReveal>
          <BlurReveal>
            <Card>
              <CardContent>
                <div className="text-xs uppercase tracking-widest text-fg-3">Rank</div>
                <div className="text-3xl font-bold tracking-tight mt-1">{rank ? `#${rank}` : "—"}</div>
                <div className="text-xs text-fg-2 mt-1">All players</div>
              </CardContent>
            </Card>
          </BlurReveal>
        </div>

        <BlurReveal delay={0.15}>
          <section className="mt-8">
            <h2 className="text-xs uppercase tracking-widest text-fg-3 mb-3">Display name</h2>
            <Card>
              <CardContent>
                <form onSubmit={onSave} className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
                  <label className="block flex-1">
                    <span className="block text-xs text-fg-3 mb-2">Shown on leaderboards</span>
                    <Input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" maxLength={64} />
                  </label>
                  <Button type="submit" size="md" disabled={saving || !name.trim() || name.trim() === profile?.full_name}>
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Save
                  </Button>
                </form>
                {msg && (
                  <div role="alert" className={
                    "mt-3 inline-flex items-center gap-2 text-sm " +
                    (msg.kind === "ok" ? "text-ok" : "text-bad")
                  }>
                    {msg.kind === "ok" ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                    {msg.text}
                  </div>
                )}
              </CardContent>
            </Card>
          </section>
        </BlurReveal>

        <BlurReveal delay={0.2}>
          <section className="mt-6">
            <h2 className="text-xs uppercase tracking-widest text-fg-3 mb-3">Account</h2>
            <Card className="p-0 divide-y divide-line">
              <ActionRow icon={KeyRound}    title="Change password" sub="Current password required"      to="/reset-password" />
              <ActionRow icon={Link2}       title="Linked accounts" sub="Google and Microsoft sign-in"   to="/profile" />
              <ActionRow icon={ShieldCheck} title="Two-factor auth" sub="Email OTP, remember device"     to="/mfa-verify" />
              <ActionRow icon={BellRing}    title="Notifications"   sub="Streak reminders, weekly recap" to="/profile" />
            </Card>
          </section>
        </BlurReveal>

        <BlurReveal delay={0.25}>
          <Separator className="my-8" />
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div>
              <h2 className="font-semibold text-bad">Danger zone</h2>
              <p className="text-sm text-fg-2 mt-1">Account deletion with 30-day recovery. Your data is held, then permanently removed.</p>
            </div>
            <Button variant="outline" size="md" className="md:ml-auto text-bad border-bad/40 hover:bg-bad/10" onClick={onDelete}>
              <Trash2 className="w-4 h-4" /> Delete account
            </Button>
          </div>
          <p className="mt-6 text-xs text-fg-3">
            By using Arabise you agree to our <Link to="/terms" className="text-fg-2 hover:text-fg">Terms</Link> and{" "}
            <Link to="/privacy" className="text-fg-2 hover:text-fg">Privacy Policy</Link>.
          </p>
        </BlurReveal>
        </div>
      </div>
    </PageFrame>
  );
}

function ActionRow({ icon: Icon, title, sub, to }: { icon: typeof User; title: string; sub: string; to: string }) {
  return (
    <Link to={to}
      className="w-full flex items-center gap-4 px-5 py-4 hover:bg-bg-3 text-left transition-colors first:rounded-t-2xl last:rounded-b-2xl">
      <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-bg border border-line text-fg-2">
        <Icon className="w-4 h-4" />
      </span>
      <span className="flex-1">
        <span className="block text-sm font-medium">{title}</span>
        <span className="block text-xs text-fg-2">{sub}</span>
      </span>
      <ChevronRight className="w-4 h-4 text-fg-3" />
    </Link>
  );
}
